import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, CardHeader, CardBody } from 'reactstrap';
import { MarketplacePricing } from '../../slices/marketplace/pricing/models/marketplace-pricing.model';
import RequestPreOrderEngagementAgreementModal from './RequestPreOrderEngagementAgreementModal/RequestPreOrderEngagementAgreementModal';
import createNotification from '../../utils/createNotification';
import FeeCalculatorModal from './FeeCalculatorModal/FeeCalculatorModal';
import { useStateSelector } from '../../store/selectors';
import {
    ComingSoonCommercialTermsTableData,
    ComingSoonQuote,
} from './RequestPreOrderEngagementAgreementModal/Models/Quote';
import {
    ComingSoonCommercialTermsTable,
    FreemiumFeeRanges,
} from './ComingSoonCommercialTermsTable/ComingSoonCommercialTermsTable';
import './ComingSoonQuotesComponent.scss';
import { UserSummaryPracticeDTO } from '../Practices/practiceDataMapper';
import { FeeCalculator } from './FeeCalculatorModal/FeeCalculator';
import { BrokerageType } from '../../slices/marketplace/pricing/models/brokerage-type.enum';

interface ComingSoonQuotesComponentProps {
    data: ComingSoonQuote;
    marketplacePricing: MarketplacePricing;
    fteW2Dvms: string;
    ltmRevenue: string;
    practice: UserSummaryPracticeDTO;
    hasPaidSubscription: boolean;
}

const ComingSoonQuotesComponent = (props: ComingSoonQuotesComponentProps) => {
    const [data, setData] = useState(props.data);
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [isRequestModalVisible, setIsRequestModalVisible] = useState(false);

    const [isFeeCalculatorModalVisible, setIsFeeCalculatorModalVisible] =
        useState(false);

    useEffect(() => {
        setData(props.data);
    }, [props]);

    const freemiumFeeRanges = useMemo((): FreemiumFeeRanges | undefined => {
        if (props.hasPaidSubscription) return undefined;

        const ltmRevenue = Number(props.ltmRevenue?.replace(/[^0-9]/g, '') || 0);
        const fteDvms = Number(props.fteW2Dvms || 0);

        if (!ltmRevenue || !fteDvms || !marketplacePricing) return undefined;

        const feeCalculator = new FeeCalculator(marketplacePricing);
        const feeTableResult = feeCalculator.CalculateDiscountBrokerageFees({
            brokerageType: props.data.type as BrokerageType,
            ltmRevenue: ltmRevenue,
            fteDvms: fteDvms,
            idNdaFee: 2,
            dataNdaFee: 2,
            financialModelFee: 2,
            buyerMeetings: 2,
            meetingType: 'inPerson',
        });

        const pricing =
            props.data.type === BrokerageType.Discount
                ? marketplacePricing.discountBrokeragePricing
                : marketplacePricing.fullServiceBrokeragePricing;

        return {
            transactionFeeMin: feeTableResult.transactionFees.value1,
            transactionFeeMax: feeTableResult.transactionFees.value5,
            processFeeMin: feeTableResult.processFees.value1,
            processFeeMax: feeTableResult.processFees.value5,
            tailPeriod: `${pricing.transactionFees.tailPeriodMonths} Months`,
        };
    }, [props.hasPaidSubscription, props.ltmRevenue, props.fteW2Dvms, props.data.type, marketplacePricing]);

    const isFormFilledIn = () => {
        const { fteW2Dvms, ltmRevenue } = props;

        const hasSelectedPractice = props.practice?.id > 0;

        const hasFteAndRevenue =
            fteW2Dvms !== undefined &&
            fteW2Dvms !== '' &&
            ltmRevenue !== undefined &&
            ltmRevenue !== '';

        const areCommercialTermsFilled = data.leftText;
        return (
            hasSelectedPractice && hasFteAndRevenue && areCommercialTermsFilled
        );
    };

    const details = (data: ComingSoonCommercialTermsTableData) => {
        return (
            <ComingSoonCommercialTermsTable
                data={data}
                hasPaidSubscription={props.hasPaidSubscription}
                freemiumFeeRanges={freemiumFeeRanges}
            />
        );
    };

    function createMarkup(desc: string) {
        return { __html: desc };
    }

    return (
        <div className="quotes coming-soon-quotes">
            <Card className="card-section main-card mb-3">
                <CardHeader>
                    <span className="practice-heading">{data.title}</span>
                </CardHeader>
                <CardBody>
                    <div className="discount_brokerage">
                        <Row className="custom-row">
                            <Col md={6} className="left-col">
                                <div className="services_content">
                                    <h6>{data.rightColumnTitle}</h6>
                                    <span
                                        dangerouslySetInnerHTML={createMarkup(
                                            data.rightText
                                        )}></span>
                                </div>
                            </Col>
                            <Col md={6} className="right-col">
                                <div className="commerical_term">
                                    <h6>{data.leftColumnTitle}</h6>
                                    <div className="content_c__term mt-2">
                                        {data.leftText
                                            ? details(data.leftText)
                                            : 'NA'}
                                    </div>
                                </div>
                            </Col>
                            <Col md={12}>
                                <div className="coming-soon-btns-wrapper mt-3 text-center">
                                    <button
                                        className="btn btn-primary"
                                        disabled={!isFormFilledIn()}
                                        onClick={() => {
                                            if (isFormFilledIn()) {
                                                setIsRequestModalVisible(true);
                                            } else {
                                                createNotification(
                                                    'An error ocurred while processing request.',
                                                    'error'
                                                );
                                            }
                                        }}>
                                        {
                                            marketplacePricing.requestPreorderAgreementButtonText
                                        }
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        disabled={!isFormFilledIn()}
                                        onClick={() =>
                                            setIsFeeCalculatorModalVisible(
                                                true
                                            )
                                        }>
                                        Fee Calculator
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </CardBody>
            </Card>
            {isRequestModalVisible ? (
                <RequestPreOrderEngagementAgreementModal
                    isVisible={isRequestModalVisible}
                    practice={props.practice}
                    closeModal={() => {
                        setIsRequestModalVisible(false);
                    }}
                    brokerage={
                        props.data.type == 1 ? 'Discount' : 'Full-Service'
                    }
                    fteW2Dvms={Number(props.fteW2Dvms)}
                    ltmRevenue={Number(
                        props.ltmRevenue?.replace(/[^0-9]/g, '')
                    )}
                />
            ) : (
                <></>
            )}
            {isFeeCalculatorModalVisible && (
                <FeeCalculatorModal
                    isVisible={isFeeCalculatorModalVisible}
                    closeModal={() => setIsFeeCalculatorModalVisible(false)}
                    fteW2Dvms={props.fteW2Dvms}
                    ltmRevenue={props.ltmRevenue}
                    brokerageType={props.data.type}
                    isFreeMember={!props.hasPaidSubscription}
                />
            )}
        </div>
    );
};

export default ComingSoonQuotesComponent;
